import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WhatsAppIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

const emptyForm = { first_name: "", email: "", phone: "", company: "", pack: "" };

const fieldCls =
  "w-full rounded-xl border border-ink-line bg-ink-soft/80 px-4 py-3 text-sm text-white placeholder:text-mist/50 outline-none transition-all duration-200 focus:border-viol/60 focus:ring-2 focus:ring-viol/25";

export const DemoFormSection = ({ pack, setPack }) => {
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/demo-requests`, {
        ...form,
        pack: form.pack || null,
      });
      setDone(data);
      setForm(emptyForm);
      setPack("");
      toast.success("Demande envoyée. On revient vers vous très vite.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d?.msg || "").filter(Boolean).join(" ")
          : "Erreur d'envoi. Vérifiez votre connexion et réessayez.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="demo" className="relative py-24 sm:py-32 scroll-mt-20">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-viol-soft mb-4">04 — Démo</p>
            <h2 className="font-display font-bold text-3xl sm:text-5xl text-white leading-tight">
              Voyez Giulia sur votre marché.
            </h2>
            <p className="mt-5 max-w-md text-mist leading-relaxed">
              30 minutes, en visio ou au téléphone, pour comprendre ce que
              Giulia peut faire pour votre entreprise. Sans engagement.
            </p>

            <a
              href="https://wa.me/33660705559"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-btn"
              className="mt-9 inline-flex items-center gap-3 rounded-full border border-viol/35 bg-viol/10 px-6 py-3.5 text-sm font-medium text-viol-pale transition-all duration-300 hover:bg-viol/20 hover:border-viol/60 hover:-translate-y-0.5"
            >
              <span className="text-[#4ADE80]">
                <WhatsAppIcon />
              </span>
              Ou écrivez-nous sur WhatsApp
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="glass rounded-3xl p-7 sm:p-9"
          >
            {done ? (
              <div className="text-center py-10" data-testid="demo-form-success">
                <CheckCircle2 size={52} className="mx-auto text-viol-soft" />
                <h3 className="mt-6 font-display font-bold text-2xl text-white">
                  Merci {done.first_name} !
                </h3>
                <p className="mt-3 text-sm text-mist leading-relaxed">
                  Votre demande pour {done.company} est bien enregistrée.
                  On revient vers vous très vite pour caler la démo.
                </p>
                <button
                  data-testid="demo-form-reset"
                  onClick={() => setDone(null)}
                  className="mt-8 text-sm text-viol-pale underline underline-offset-8 decoration-viol/40 hover:decoration-viol"
                >
                  Faire une autre demande
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="demo-form">
                <div>
                  <label htmlFor="df-name" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-mist">
                    Prénom *
                  </label>
                  <input
                    id="df-name"
                    data-testid="demo-form-input-firstname"
                    required
                    maxLength={80}
                    value={form.first_name}
                    onChange={set("first_name")}
                    className={fieldCls}
                    placeholder="Votre prénom"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="df-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-mist">
                      Email *
                    </label>
                    <input
                      id="df-email"
                      data-testid="demo-form-input-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={set("email")}
                      className={fieldCls}
                      placeholder="vous@entreprise.fr"
                    />
                  </div>
                  <div>
                    <label htmlFor="df-phone" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-mist">
                      Téléphone *
                    </label>
                    <input
                      id="df-phone"
                      data-testid="demo-form-input-phone"
                      type="tel"
                      required
                      minLength={6}
                      maxLength={30}
                      value={form.phone}
                      onChange={set("phone")}
                      className={fieldCls}
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="df-company" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-mist">
                    Entreprise *
                  </label>
                  <input
                    id="df-company"
                    data-testid="demo-form-input-company"
                    required
                    maxLength={120}
                    value={form.company}
                    onChange={set("company")}
                    className={fieldCls}
                    placeholder="Nom de votre entreprise"
                  />
                </div>
                <div>
                  <label htmlFor="df-pack" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-mist">
                    Pack qui intéresse (optionnel)
                  </label>
                  <select
                    id="df-pack"
                    data-testid="demo-form-input-pack"
                    value={pack || form.pack}
                    onChange={(e) => {
                      setPack(e.target.value);
                      setForm({ ...form, pack: e.target.value });
                    }}
                    className={`${fieldCls} appearance-none ${!pack && !form.pack ? "text-mist/50" : "text-white"}`}
                  >
                    <option value="">Sélectionner (optionnel)</option>
                    <option value="Giulia Contenu">Giulia Contenu</option>
                    <option value="Giulia Clients">Giulia Clients</option>
                    <option value="Giulia Complète">Giulia Complète</option>
                    <option value="Je ne sais pas encore">Je ne sais pas encore</option>
                  </select>
                </div>
                <button
                  type="submit"
                  data-testid="demo-form-submit"
                  disabled={sending}
                  className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-gradient-to-r from-iris-deep to-viol px-7 py-4 text-base font-medium text-white shadow-[0_0_32px_rgba(139,92,246,0.35)] transition-all duration-300 hover:shadow-[0_0_48px_rgba(139,92,246,0.55)] hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                >
                  {sending ? (
                    <>
                      Envoi en cours
                      <Loader2 size={18} className="animate-spin" />
                    </>
                  ) : (
                    <>
                      Demander une démo
                      <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DemoFormSection;
